import type {
  ManagedPr,
  WorkspaceArtifact,
  WorkspaceArtifactId,
  WorkspaceArtifactKind,
} from "../types";
import {
  isOlderProofRun,
  isProofReportMetadata,
} from "@/components/codesmith/proof/proofReport";
import {
  earlierRoundArtifactId,
  proofReportArtifactId,
} from "@/components/codesmith/proof/proofReportArtifactId";
import { reviewReportArtifactId } from "@/components/codesmith/review/reviewReportArtifactId";
import { workspaceArtifactSearchableFields } from "./workspaceArtifactKinds";

type PrArtifact = Extract<WorkspaceArtifact, { kind: "pr" }>;

type ProofArtifact = Extract<WorkspaceArtifact, { kind: "proof_report" }>;

export type ReportArtifact = Extract<
  WorkspaceArtifact,
  { kind: "proof_report" | "review_report" }
>;

export interface WorkspaceCatalog {
  combinedPrView: boolean;
  /** One entry per tab: system artifacts first; dated artifacts oldest first. */
  ordered: readonly WorkspaceArtifact[];
  byId: ReadonlyMap<WorkspaceArtifactId, WorkspaceArtifact>;
  /** Every listed artifact by its own id, each run of a report tab included. */
  artifacts: ReadonlyMap<WorkspaceArtifactId, WorkspaceArtifact>;
  /** PR runs: Proof newest first, then Review newest first; report aliases keep their own runs. */
  runs: ReadonlyMap<WorkspaceArtifactId, readonly ReportArtifact[]>;
  tabIds: ReadonlyMap<WorkspaceArtifactId, WorkspaceArtifactId>;
}

/** Matches SessionArtifactCatalog::CHANGES_ID. */
export const CHANGES_ARTIFACT_ID: WorkspaceArtifactId = "changes";

/** Client-only; the backend lists UUIDs and CHANGES_ARTIFACT_ID, so it cannot collide. */
export const PROJECT_ARTIFACT_ID: WorkspaceArtifactId = "project";

export const EMPTY_WORKSPACE_CATALOG: WorkspaceCatalog = {
  combinedPrView: true,
  ordered: [],
  byId: new Map(),
  artifacts: new Map(),
  runs: new Map(),
  tabIds: new Map(),
};

/** Changes and anything else listed without an arrival time, including a session's project. */
export function isSystemArtifact(artifact: WorkspaceArtifact): boolean {
  return artifact.createdAt === null;
}

/** The tab for the Codesmith Project a session belongs to, coordinator or member alike. */
export function projectArtifact(
  projectId: string,
  org: string,
): Extract<WorkspaceArtifact, { kind: "project" }> {
  return {
    id: PROJECT_ARTIFACT_ID,
    kind: "project",
    title: "Project",
    createdAt: null,
    projectId,
    org,
  };
}

/** A project coordinator's tabs for its agents' open and draft PRs, titled as SessionArtifactCatalog titles its own. */
export function projectMemberPrArtifacts(
  prs: readonly ManagedPr[],
): PrArtifact[] {
  return prs
    .filter((pr) => pr.status === "open" || pr.status === "draft")
    .map((pr): PrArtifact => ({
      id: pr.id,
      kind: "pr",
      title: pr.title?.trim() || `${pr.repository}#${pr.pr_number}`,
      createdAt: new Date(pr.created_at),
      repository: pr.repository,
      prNumber: pr.pr_number,
    }));
}

/** Kinds that show as tabs; a new kind stays hidden until added here. */
export const WORKSPACE_TAB_ARTIFACT_KINDS: ReadonlySet<WorkspaceArtifactKind> =
  new Set<WorkspaceArtifactKind>([
    "changes",
    "project",
    "pr",
    "canvas",
    "dream_report",
    "review_report",
    "proof_report",
  ]);

/** What the pane shows without the diff viewer: everything else reads a sandbox or a diff. */
export const CANVAS_ONLY_TAB_ARTIFACT_KINDS: ReadonlySet<WorkspaceArtifactKind> =
  new Set<WorkspaceArtifactKind>(["canvas"]);

/** The session's artifacts narrowed to the kinds that show as tabs. */
export function artifactsShownAsTabs(
  artifacts: readonly WorkspaceArtifact[],
  kinds: ReadonlySet<WorkspaceArtifactKind> = WORKSPACE_TAB_ARTIFACT_KINDS,
): WorkspaceArtifact[] {
  return artifacts.filter((artifact) => kinds.has(artifact.kind));
}

/**
 * Every Proof run by id, whether or not Proof shows as a tab. Without a
 * catalog (the server omits it while both pane flags are off) the
 * transcript's runs stand alone.
 */
export function proofRunsById(
  merged: readonly WorkspaceArtifact[] | undefined,
  transcript: readonly WorkspaceArtifact[],
): ReadonlyMap<WorkspaceArtifactId, ProofArtifact> {
  const byId = new Map<WorkspaceArtifactId, ProofArtifact>();
  for (const artifact of merged ?? transcript) {
    if (artifact.kind === "proof_report") byId.set(artifact.id, artifact);
  }
  return byId;
}

/**
 * How much of a Proof or review child's outcome an entry carries, null for
 * any other kind: a report beats a placeholder, and for Proof a settled
 * report beats a live snapshot, which beats the pending run standing in
 * before the first frame.
 */
function contentRank(artifact: WorkspaceArtifact): number | null {
  switch (artifact.kind) {
    case "review_report":
      return artifact.report === undefined ? 0 : 1;
    case "proof_report":
      if (isProofReportMetadata(artifact.run)) return 2;
      return artifact.run.status === "planning" &&
        artifact.run.cases.length === 0
        ? 0
        : 1;
    default:
      return null;
  }
}

function reportAsOf(artifact: WorkspaceArtifact): number | undefined {
  return artifact.kind === "review_report" || artifact.kind === "proof_report"
    ? artifact.reportAsOf?.getTime()
    : undefined;
}

/**
 * Whether `candidate`'s report is fresher than `listed`'s. A catalog report
 * is the child's latest as of the newest message read with it, so a
 * transcript report wins only when it landed after that; a report with no
 * known freshness loses to one that has it, e.g. `transcript@10:05` beats
 * `catalog@10:04` but not `catalog@10:06`.
 */
function isFresherReport(
  candidate: WorkspaceArtifact,
  listed: WorkspaceArtifact,
): boolean {
  const candidateAsOf = reportAsOf(candidate);
  const listedAsOf = reportAsOf(listed);
  if (listedAsOf === undefined) return true;
  return candidateAsOf !== undefined && candidateAsOf > listedAsOf;
}

function isSameProofRound(a: ProofArtifact, b: ProofArtifact): boolean {
  return (
    (a.run.run_id !== undefined && a.run.run_id === b.run.run_id) ||
    (a.roundId !== undefined && a.roundId === b.roundId)
  );
}

function earliest(a: Date | null, b: Date | null): Date | null {
  if (a === null || b === null) return a ?? b;
  return a.getTime() <= b.getTime() ? a : b;
}

/**
 * Server artifacts plus client-derived ones: system artifacts first, then
 * everything dated oldest first. A Proof or review child both sources list
 * keeps the entry with more content, so a placeholder derived from a spawn
 * row never masks a report, and vice versa. Between two reports the fresher
 * wins (see isFresherReport): the transcript can carry a follow-up live
 * before the refetch lists it, and can also hold an older row the catalog
 * has since superseded. Between placeholders the server's wins for its
 * status. A child keeps the earlier date: a run is ordered by when it
 * started, and the transcript dates it by its report when its spawn row is
 * not loaded. Any other repeated id keeps its newest entry, the earlier
 * listing on a tie.
 */
export function mergeWorkspaceArtifacts(
  server: readonly WorkspaceArtifact[],
  derived: readonly WorkspaceArtifact[],
): readonly WorkspaceArtifact[] {
  if (derived.length === 0) return server;

  const time = (artifact: WorkspaceArtifact) =>
    artifact.createdAt?.getTime() ?? 0;

  const byId = new Map<WorkspaceArtifactId, WorkspaceArtifact>();
  for (const artifact of [...server, ...derived]) {
    const listed = byId.get(artifact.id);
    if (listed === undefined) {
      byId.set(artifact.id, artifact);
      continue;
    }

    if (
      artifact.kind === "proof_report" &&
      listed.kind === "proof_report" &&
      artifact.run.run_id !== undefined &&
      listed.run.run_id !== undefined &&
      artifact.run.run_id !== listed.run.run_id
    ) {
      // Runs without start times cannot be ordered; then the fresher report wins.
      const artifactIsNewer =
        isOlderProofRun(listed.run, artifact.run) ||
        (!isOlderProofRun(artifact.run, listed.run) &&
          isFresherReport(artifact, listed));
      const [older, newer] = artifactIsNewer
        ? [listed, artifact]
        : [artifact, listed];
      const historicalId = isProofReportMetadata(older.run)
        ? earlierRoundArtifactId(older)
        : null;
      if (historicalId !== null) {
        byId.set(historicalId, {
          ...older,
          id: historicalId,
          earlierRound: true,
          status: "ready",
        });
      }
      byId.set(artifact.id, newer);
      continue;
    }

    const rank = contentRank(artifact);
    const listedRank = contentRank(listed);
    if (rank === null || listedRank === null) {
      if (time(artifact) > time(listed)) byId.set(artifact.id, artifact);
      continue;
    }

    const winner =
      rank > listedRank ||
      (rank === listedRank && rank > 0 && isFresherReport(artifact, listed))
        ? artifact
        : listed;

    const createdAt =
      winner.kind === "proof_report" && winner.run.started_at
        ? new Date(winner.run.started_at)
        : earliest(listed.createdAt, artifact.createdAt);
    byId.set(
      artifact.id,
      winner.createdAt === createdAt ? winner : { ...winner, createdAt },
    );
  }

  // A late older mirror can make the transcript list the current run as history too.
  for (const [id, artifact] of byId) {
    if (artifact.kind !== "proof_report" || !artifact.earlierRound) continue;
    const current = byId.get(proofReportArtifactId(artifact.childSessionId));
    if (
      current?.kind === "proof_report" &&
      isSameProofRound(artifact, current)
    ) {
      byId.delete(id);
    }
  }

  const all = [...byId.values()];
  const system = all.filter(isSystemArtifact);
  const dated = all
    .filter((artifact) => !isSystemArtifact(artifact))
    .sort(
      (a, b) => (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0),
    );

  return [...system, ...dated];
}

function isReportArtifact(
  artifact: WorkspaceArtifact,
): artifact is ReportArtifact {
  return artifact.kind === "proof_report" || artifact.kind === "review_report";
}

/** A saved report its child has since replaced. */
export function isEarlierRound(artifact: ReportArtifact): boolean {
  return artifact.earlierRound === true;
}

/** Each earlier round moves right behind its child's newest run, keeping its order among the child's rounds. */
function roundsBehindNewest(
  runs: readonly ReportArtifact[],
): readonly ReportArtifact[] {
  const rounds = runs.filter(isEarlierRound);
  return runs
    .filter((run) => !isEarlierRound(run))
    .flatMap((run) => [
      run,
      ...rounds.filter((round) => round.childSessionId === run.childSessionId),
    ]);
}

/**
 * e.g. `proof:acme/api#41`. Without a repository (non-github.com PR URLs) a
 * review still tabs by its child, so earlier rounds join the child's tab.
 */
export function reportTabId(
  artifact: ReportArtifact,
  combinedPrView = true,
): WorkspaceArtifactId {
  if (
    artifact.repository === null ||
    (combinedPrView && artifact.kind === "review_report" && artifact.local)
  ) {
    return artifact.kind === "review_report"
      ? reviewReportArtifactId(artifact.childSessionId)
      : proofReportArtifactId(artifact.childSessionId);
  }
  const kind = artifact.kind === "proof_report" ? "proof" : "review";
  return `${kind}:${artifact.repository.toLowerCase()}#${artifact.prNumber}`;
}

/** The alias for a PR tab's diff view, e.g. `changes:acme/api#41`. */
export function prChangesTabId(
  artifact: Extract<WorkspaceArtifact, { kind: "pr" }>,
): WorkspaceArtifactId {
  return `changes:${artifact.repository.toLowerCase()}#${artifact.prNumber}`;
}

/** The tab `id` opens in, e.g. `proof-report:ses_1` → its PR's tab; any other id is its own tab. */
export function tabIdOf(
  catalog: WorkspaceCatalog,
  id: WorkspaceArtifactId,
): WorkspaceArtifactId {
  return catalog.tabIds.get(id) ?? id;
}

/**
 * Index the session's artifact list, which lists dated artifacts oldest
 * first; a repeated id keeps its first listing. A report tab is listed as
 * its newest run, under the tab's id.
 */
export function buildWorkspaceCatalog(
  artifacts: readonly WorkspaceArtifact[] | undefined,
  { combinedPrView = true }: { combinedPrView?: boolean } = {},
): WorkspaceCatalog {
  if (artifacts === undefined || artifacts.length === 0) {
    return combinedPrView
      ? EMPTY_WORKSPACE_CATALOG
      : { ...EMPTY_WORKSPACE_CATALOG, combinedPrView };
  }
  const listed = new Map<WorkspaceArtifactId, WorkspaceArtifact>();
  for (const artifact of artifacts) {
    if (!listed.has(artifact.id)) listed.set(artifact.id, artifact);
  }

  const runs = new Map<WorkspaceArtifactId, ReportArtifact[]>();
  for (const artifact of listed.values()) {
    if (!isReportArtifact(artifact)) continue;
    const tabId = reportTabId(artifact, combinedPrView);
    runs.set(tabId, [artifact, ...(runs.get(tabId) ?? [])]);
  }

  for (const [tabId, tabRuns] of runs) {
    const newestFirst = tabRuns.sort(
      (a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0),
    );
    runs.set(tabId, [...roundsBehindNewest(newestFirst)]);
  }

  const prs = new Map<string, Extract<WorkspaceArtifact, { kind: "pr" }>>();
  const prKey = (repository: string, prNumber: number) =>
    `pr:${repository.toLowerCase()}#${prNumber}`;
  for (const artifact of listed.values()) {
    if (artifact.kind === "pr") {
      const key = prKey(artifact.repository, artifact.prNumber);
      if (!prs.has(key)) prs.set(key, artifact);
    }
  }
  const tabIds = new Map<WorkspaceArtifactId, WorkspaceArtifactId>();
  const ordered: WorkspaceArtifact[] = [];
  const emitted = new Set<WorkspaceArtifactId>();
  for (const artifact of [...listed.values()]) {
    let tab: WorkspaceArtifact = artifact;
    if (artifact.kind === "pr") {
      tab = combinedPrView
        ? (prs.get(prKey(artifact.repository, artifact.prNumber)) ?? artifact)
        : artifact;
      tabIds.set(prKey(artifact.repository, artifact.prNumber), tab.id);
    } else if (isReportArtifact(artifact)) {
      const reportId = reportTabId(artifact, combinedPrView);
      if (
        combinedPrView &&
        artifact.repository !== null &&
        !(artifact.kind === "review_report" && artifact.local)
      ) {
        const key = prKey(artifact.repository, artifact.prNumber);
        let pr = prs.get(key);
        if (pr === undefined) {
          pr = {
            id: key,
            kind: "pr",
            title: `#${artifact.prNumber} ${artifact.repository
              .split("/")
              .pop()}`,
            createdAt: artifact.createdAt,
            repository: artifact.repository,
            prNumber: artifact.prNumber,
          };
          prs.set(key, pr);
          listed.set(pr.id, pr);
        }
        tab = pr;
        tabIds.set(key, pr.id);
        tabIds.set(reportId, pr.id);
      } else {
        const newest = runs.get(reportId)?.[0];
        if (newest === undefined) continue;
        tab = { ...newest, id: reportId };
      }
    }
    tabIds.set(artifact.id, tab.id);
    tabIds.set(tab.id, tab.id);
    if (artifact.kind === "pr" && !combinedPrView) {
      tabIds.set(prChangesTabId(artifact), artifact.id);
    }
    if (!emitted.has(tab.id)) {
      ordered.push(tab);
      emitted.add(tab.id);
    }
  }
  if (!combinedPrView) {
    for (const artifact of listed.values()) {
      if (!isReportArtifact(artifact) || artifact.repository === null) continue;
      const key = prKey(artifact.repository, artifact.prNumber);
      if (prs.has(key)) continue;
      const proofId = `proof:${key.slice(3)}`;
      const tabId = runs.has(proofId) ? proofId : reportTabId(artifact, false);
      tabIds.set(key, tabId);
      tabIds.set(`changes:${key.slice(3)}`, tabId);
    }
  }
  for (const pr of prs.values()) {
    const key = prKey(pr.repository, pr.prNumber).slice(3);
    if (!combinedPrView) continue;
    tabIds.set(prChangesTabId(pr), pr.id);
    tabIds.set(`proof:${key}`, pr.id);
    tabIds.set(`review:${key}`, pr.id);
    const proof = runs.get(`proof:${key}`) ?? [];
    const review = runs.get(`review:${key}`) ?? [];
    if (proof.length + review.length > 0)
      runs.set(pr.id, [...proof, ...review]);
  }

  return {
    combinedPrView,
    ordered,
    byId: new Map(ordered.map((tab) => [tab.id, tab])),
    artifacts: listed,
    runs,
    tabIds,
  };
}

/** Exact title, then title prefix, then any other field match. */
function matchRank(
  artifact: WorkspaceArtifact,
  query: string,
  catalog: WorkspaceCatalog,
): number | null {
  const title = artifact.title.toLowerCase();
  if (title === query) return 0;
  if (title.startsWith(query)) return 1;

  const members =
    artifact.kind === "pr" ? (catalog.runs.get(artifact.id) ?? []) : [];
  const fields = [artifact, ...members].flatMap(
    workspaceArtifactSearchableFields,
  );
  const matches = fields.some((field) => field.toLowerCase().includes(query));
  return matches ? 2 : null;
}

/**
 * Conversation-scoped search over the whole catalog, open tabs or not.
 * Empty query: system artifacts, then the rest newest first.
 * e.g. `"RUNNER-util"` → both revisions of `runner-utilization.canvas.tsx`.
 */
export function searchWorkspaceArtifacts(
  catalog: WorkspaceCatalog,
  query: string,
): WorkspaceArtifact[] {
  const normalized = query.trim().toLowerCase();
  if (normalized === "") {
    const system = catalog.ordered.filter(isSystemArtifact);
    const dated = catalog.ordered.filter((a) => !isSystemArtifact(a)).reverse();
    return [...system, ...dated];
  }

  return catalog.ordered
    .map((artifact, position) => ({
      artifact,
      position,
      rank: matchRank(artifact, normalized, catalog),
    }))
    .filter(
      (match): match is typeof match & { rank: number } => match.rank !== null,
    )
    .sort((a, b) => a.rank - b.rank || a.position - b.position)
    .map((match) => match.artifact);
}
